import React, { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight, MoreHorizontal, ChevronDown } from 'lucide-react';

export default function Pagination({
  currentPage,
  pageSize,
  totalRecords,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 25, 50, 100, 'All']
}) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));

  const handlePrev = () => {
    if (currentPage > 1) onPageChange(currentPage - 1);
  };
  const handleNext = () => {
    if (currentPage < totalPages) onPageChange(currentPage + 1);
  };

  const handleOptionSelect = (opt) => {
    if (opt === 'All') {
      onPageSizeChange(totalRecords > 0 ? totalRecords : 999999);
    } else {
      onPageSizeChange(opt);
    }
    setIsDropdownOpen(false);
  };

  // Determine what to show on the dropdown button
  const displayPageSize =
    pageSize >= 999999 || (totalRecords > 0 && pageSize >= totalRecords) ? 'All' : pageSize;

  // Calculate rendering window for pagination pills
  const getPageNumbers = () => {
    let pages = [];
    let start = Math.max(1, currentPage - 1);
    let end = Math.min(totalPages, currentPage + 1);

    if (currentPage === 1) end = Math.min(3, totalPages);
    if (currentPage === totalPages) start = Math.max(1, totalPages - 2);

    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  const pages = getPageNumbers();

  return (
    <div className="d-flex flex-column flex-sm-row align-items-center justify-content-between px-4 py-3 bg-white border-top gap-3">
      {/* Settings & Info */}
      <div className="d-flex align-items-center small text-muted z-1">
        <span className="me-3 fw-medium">Rows per page:</span>

        {/* Custom Fancy Dropdown */}
        <div className="position-relative" ref={dropdownRef}>
          <button
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="btn btn-sm d-flex align-items-center justify-content-between shadow-none fw-semibold"
            style={{
              minWidth: '72px',
              border: '1.5px solid var(--primary)',
              color: 'var(--primary)',
              background: 'white'
            }}
          >
            <span className="fw-medium me-2">{displayPageSize}</span>
            <ChevronDown
              size={14}
              className={`transition-transform border-0 ${isDropdownOpen ? 'rotate-180' : ''}`}
              style={{ transition: 'transform 0.2s' }}
            />
          </button>

          {isDropdownOpen && (
            <div
              className="dropdown-menu show position-absolute bottom-100 start-0 mb-1 w-100 shadow-sm border"
              style={{ minWidth: '72px', zIndex: 1050 }}
            >
              {pageSizeOptions.map((opt) => (
                <button
                  key={opt}
                  onClick={() => handleOptionSelect(opt)}
                  className={`dropdown-item small py-2 px-3 ${
                    displayPageSize === opt ? 'fw-semibold' : 'text-secondary'
                  }`}
                  style={
                    displayPageSize === opt
                      ? { backgroundColor: 'var(--primary)', color: '#fff' }
                      : {}
                  }
                >
                  {opt}
                </button>
              ))}
            </div>
          )}
        </div>

        <span className="d-none d-sm-inline ms-4">
          Showing{' '}
          <span className="fw-bold text-dark">
            {totalRecords > 0 ? (currentPage - 1) * pageSize + 1 : 0}
          </span>{' '}
          to{' '}
          <span className="fw-bold text-dark">
            {Math.min(currentPage * pageSize, totalRecords)}
          </span>{' '}
          of <span className="fw-bold text-dark">{totalRecords}</span> entries
        </span>
      </div>

      {/* Pages Controls */}
      <div className="d-flex align-items-center gap-1 position-relative" style={{ zIndex: 0 }}>
        <button
          onClick={handlePrev}
          disabled={currentPage === 1}
          className="btn btn-light btn-sm p-2 rounded text-secondary shadow-none d-flex align-items-center"
        >
          <ChevronLeft size={20} />
        </button>

        {pages[0] > 1 && (
          <>
            <button
              onClick={() => onPageChange(1)}
              className="btn btn-light btn-sm fw-semibold d-flex align-items-center justify-content-center"
              style={{ width: '36px', height: '36px' }}
            >
              1
            </button>
            {pages[0] > 2 && <MoreHorizontal size={16} className="text-muted mx-1" />}
          </>
        )}

        {pages.map((p) => {
          const isActive = p === currentPage;
          return (
            <button
              key={p}
              onClick={() => onPageChange(p)}
              className={`btn btn-sm d-flex align-items-center fw-semibold justify-content-center shadow-none ${
                isActive ? 'text-white shadow-sm' : 'btn-light text-secondary'
              }`}
              style={
                isActive
                  ? {
                      width: '36px',
                      height: '36px',
                      backgroundColor: 'var(--primary)',
                      borderColor: 'var(--primary)'
                    }
                  : { width: '36px', height: '36px' }
              }
            >
              {p}
            </button>
          );
        })}

        {pages[pages.length - 1] < totalPages && (
          <>
            {pages[pages.length - 1] < totalPages - 1 && (
              <MoreHorizontal size={16} className="text-muted mx-1" />
            )}
            <button
              onClick={() => onPageChange(totalPages)}
              className="btn btn-light btn-sm fw-semibold d-flex align-items-center justify-content-center"
              style={{ width: '36px', height: '36px' }}
            >
              {totalPages}
            </button>
          </>
        )}

        <button
          onClick={handleNext}
          disabled={currentPage === totalPages || totalRecords === 0}
          className="btn btn-light btn-sm p-2 rounded text-secondary shadow-none d-flex align-items-center"
        >
          <ChevronRight size={20} />
        </button>
      </div>
    </div>
  );
}
